import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  TextField,
  IconButton,
  Avatar,
  LinearProgress,
  Paper,
  InputAdornment,
  Tooltip,
  Chip,
  Collapse,
  Badge,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Checkbox,
  Button,
  Divider,
  CircularProgress
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import GroupIcon from '@mui/icons-material/Group';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import LogoutIcon from '@mui/icons-material/Logout';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CallIcon from '@mui/icons-material/Call';
import SearchIcon from '@mui/icons-material/Search';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import useApi from 'hooks/useApi';
import { useOutgoingCall } from 'contexts/OutgoingCallContext';
import useFirebase from 'hooks/useFirebase';
import { ref as dbRef, onChildAdded } from 'firebase/database';
import { ChatThread, ChatMessage, ChatParticipant, ChatAttachment, ChatMention } from 'types/chat';
import { useLanguage } from 'contexts/LanguageContext';
import { smartTranslate } from 'utils/smartTranslate';

import MarkdownMessage from './MarkdownMessage';
import ChatEditor from './ChatEditor';
import { htmlToMarkdown, copyRichText } from './chatUtils';

interface InlineChatWindowProps {
  open: boolean;
  onClose: () => void;
  onClosePanel?: () => void;
  thread: ChatThread;
  transactionKey?: string;
  windowIndex?: number;
  embedded?: boolean;
}

// Memory optimization constants
const MAX_MESSAGES_IN_MEMORY = 100; // Limit messages to prevent memory bloat
const POLL_INTERVAL_MS = 5000; // Slightly slower polling to reduce API calls

// Lazy-loading image component that fetches signed S3 URLs
const SignedImage = ({ filePath, fileName, fallbackUrl, getSignedUrl, style }: {
  filePath: string; fileName: string; fallbackUrl: string;
  getSignedUrl: (filePath: string) => Promise<string | null>;
  style?: React.CSSProperties;
}) => {
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);
  const attempted = useRef(false);
  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;
    getSignedUrl(filePath).then(url => {
      setSrc(url || fallbackUrl);
    }).catch(() => setSrc(fallbackUrl));
  }, [filePath, fallbackUrl, getSignedUrl]);

  if (error || !src) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'grey.100', borderRadius: 1, ...style, maxHeight: 'auto' }}>
        <ImageIcon sx={{ fontSize: 20, color: 'grey.500' }} />
        <Typography variant="caption" color="text.secondary" noWrap>{fileName}</Typography>
      </Box>
    );
  }

  return <img src={src} alt={fileName} style={style} onError={() => setError(true)} />;
};

const InlineChatWindow = ({
  open,
  onClose,
  onClosePanel,
  thread,
  transactionKey,
  windowIndex = 0,
  embedded = false
}: InlineChatWindowProps) => {
  const { getChatMessages, sendChatMessage, markChatMessagesAsRead, getChatParticipants, addChatParticipants, removeChatParticipants, uploadChatFiles, getSignedDownloadUrl, getChatGroups, editChatMessage, deleteChatMessage, user, showToast } = useApi();
  const { openCallDialog } = useOutgoingCall();
  const navigate = useNavigate();
  // firebase database instance is used by the pending-communications listener
  const { database } = useFirebase();
  
  const { language } = useLanguage();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [editorHtml, setEditorHtml] = useState('');
  const [clearTrigger, setClearTrigger] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Attachment state
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Signed URL cache for image attachments
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});
  const getSignedImageUrl = useCallback(async (filePath: string) => {
    if (signedUrls[filePath]) return signedUrls[filePath];
    try {
      const response = await getSignedDownloadUrl(filePath, '');
      if (response.success) {
        const url = response.data?.url || response.data;
        setSignedUrls(prev => ({ ...prev, [filePath]: url }));
        return url;
      }
    } catch { /* fallback */ }
    return null;
  }, [signedUrls, getSignedDownloadUrl]);

  // Mention state
  const [mentions, setMentions] = useState<ChatMention[]>([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  // messageInputRef removed — ChatEditor manages its own input
  
  // Participants panel state
  const [showParticipantsPanel, setShowParticipantsPanel] = useState(false);
  const [availableParticipants, setAvailableParticipants] = useState<ChatParticipant[]>([]);
  const [selectedNewParticipants, setSelectedNewParticipants] = useState<string[]>([]);
  const [loadingParticipants, setLoadingParticipants] = useState(false);
  const [addingParticipants, setAddingParticipants] = useState(false);
  const [currentParticipants, setCurrentParticipants] = useState<ChatParticipant[]>(thread?.participants || []);
  const [participantSearchQuery, setParticipantSearchQuery] = useState('');
  const [chatGroups, setChatGroups] = useState<any[]>([]);
  const [removalDialogOpen, setRemovalDialogOpen] = useState(false);
  const [removalTargetUids, setRemovalTargetUids] = useState<string[]>([]);
  const [removalMode, setRemovalMode] = useState<'remove' | 'leave'>('remove');
  const [removalInProgress, setRemovalInProgress] = useState(false);

  // Edit/Delete state
  const [editingMessage, setEditingMessage] = useState<ChatMessage | null>(null);
  const [editText, setEditText] = useState('');
  const [editInProgress, setEditInProgress] = useState(false);
  // delete dialog state removed; delete handled via window.confirm and optimistic updates
  const [deletingMessageUid, setDeletingMessageUid] = useState<string | null>(null);
  const deleteInProgress = deletingMessageUid !== null;
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastMessageTimestamp = useRef<string | null>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const threadCreatorUid = typeof thread?.createdBy === 'string' ? thread.createdBy : thread?.createdBy?.uid;
  const currentUserUid = user?.uid;
  const isCurrentUserCreator = Boolean(currentUserUid && threadCreatorUid && currentUserUid === threadCreatorUid);
  const isCurrentUserParticipant = Boolean(currentUserUid && currentParticipants.some(participant => participant.uid === currentUserUid));
  const canManageParticipants = isCurrentUserCreator || isCurrentUserParticipant;

  const getParticipantName = (participant: ChatParticipant) => {
    const fullName = `${participant.firstName || ''} ${participant.lastName || ''}`.trim();
    return fullName || participant.email || 'Unknown participant';
  };

  const isThreadCreator = (participantUid?: string) => Boolean(participantUid && threadCreatorUid && participantUid === threadCreatorUid);

  const closeChatSurface = useCallback(() => {
    if (onClosePanel) {
      onClosePanel();
      return;
    }
    onClose();
  }, [onClose, onClosePanel]);

  const handleAccessRevoked = useCallback(() => {
    setRemovalDialogOpen(false);
    setRemovalTargetUids([]);
    setShowParticipantsPanel(false);
    showToast('You no longer have access to this chat', 'error');
    closeChatSurface();
  }, [closeChatSurface, showToast]);

  const isAccessError = (error: any) => error?.response?.status === 401 || error?.response?.status === 403;

  const queueParticipantRemoval = (participantUid: string, mode: 'remove' | 'leave') => {
    if (mode === 'leave') {
      setRemovalMode('leave');
      setRemovalTargetUids(currentUserUid ? [currentUserUid] : []);
      setRemovalDialogOpen(Boolean(currentUserUid));
      return;
    }

    if (!canManageParticipants || isThreadCreator(participantUid) || participantUid === currentUserUid) {
      return;
    }

    setRemovalMode('remove');
    setRemovalTargetUids(prev => {
      const next = prev.includes(participantUid)
        ? prev.filter(uid => uid !== participantUid)
        : [...prev, participantUid];
      setRemovalDialogOpen(next.length > 0);
      return next;
    });
  };

  const cancelRemoval = () => {
    if (removalInProgress) return;
    setRemovalDialogOpen(false);
    setRemovalTargetUids([]);
    setRemovalMode('remove');
  };
  

  useEffect(() => {
    window.dispatchEvent(new Event('force-translate'));
  }, [messages, language]);

  // Silent refresh for polling (no full loading state)
  const refreshMessages = useCallback(async () => {
    if (!thread?.uid || isMinimized) return;

    setIsRefreshing(true);
    try {
      const response = await getChatMessages(thread.uid, 1, 50);
      if (response.success) {
        const { messages: newMessages } = response.data;
        const normalizedMessages = (newMessages || []).map((message: ChatMessage) => ({
          ...message,
          uid: getMessageUid(message),
        }));
        
        if (normalizedMessages.length > 0) {
          const latestTimestamp = normalizedMessages[normalizedMessages.length - 1]?.createdAt;
          
          // Only update if we have new messages
          if (latestTimestamp !== lastMessageTimestamp.current) {
            // Limit messages in memory
            setMessages(normalizedMessages.slice(0, MAX_MESSAGES_IN_MEMORY));
            lastMessageTimestamp.current = latestTimestamp;
            
            // Only scroll if user is near bottom
            const container = messagesContainerRef.current;
            if (container) {
              const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
              if (isNearBottom) {
                setTimeout(scrollToBottom, 100);
              }
            }
          }
          
          // Mark as read silently
          markChatMessagesAsRead(thread.uid, { markAll: true });
        }
      }
    } catch (error) {
      if (isAccessError(error)) {
        handleAccessRevoked();
        return;
      }
      console.error('Error refreshing messages:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [thread?.uid, getChatMessages, markChatMessagesAsRead, isMinimized, handleAccessRevoked, isAccessError]);

  const loadMessages = useCallback(async (pageNum: number = 1, append: boolean = false) => {
    if (!thread?.uid) return;

    setLoading(true);
    try {
      const response = await getChatMessages(thread.uid, pageNum, 50);
      if (response.success) {
        const { messages: newMessages, hasMore: more } = response.data;
        const normalizedMessages = (newMessages || []).map((message: ChatMessage) => ({
          ...message,
          uid: getMessageUid(message),
        }));
        
        if (append) {
          setMessages(prev => {
            const combined = [...normalizedMessages, ...prev];
            // Limit messages in memory
            return combined.slice(0, MAX_MESSAGES_IN_MEMORY);
          });
        } else {
          // Limit initial messages
          setMessages(normalizedMessages.slice(0, MAX_MESSAGES_IN_MEMORY));
          if (normalizedMessages.length > 0) {
            lastMessageTimestamp.current = normalizedMessages[normalizedMessages.length - 1]?.createdAt;
          }
        }
        setHasMore(more);
        
        // Mark messages as read
        if (normalizedMessages.length > 0 && !isMinimized) {
          markChatMessagesAsRead(thread.uid, { markAll: true });
        }
      }
    } catch (error) {
      if (isAccessError(error)) {
        handleAccessRevoked();
        return;
      }
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  }, [thread?.uid, getChatMessages, markChatMessagesAsRead, isMinimized, handleAccessRevoked, isAccessError]);

  // Set up polling when chat window is open and not minimized
  useEffect(() => {
    if (open && thread?.uid && !isMinimized) {
      // Start polling with optimized interval
      pollIntervalRef.current = setInterval(refreshMessages, POLL_INTERVAL_MS);
      
      return () => {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
      };
    }
  }, [open, thread?.uid, isMinimized, refreshMessages]);

  // Realtime listener for chat message updates/deletes via pending-communications
  useEffect(() => {
    if (!database || !user?.uid || !thread?.uid) return;

    const notificationsRef = dbRef(database, `pending-communications/${user.uid}`);

    const handler = (snapshot: any) => {
      const raw = snapshot.val();
      if (!raw) return;
      const eventType = (raw.eventType || '').toString().toUpperCase();

      const relevantTypes = ['CHAT_MESSAGE_UPDATED', 'CHAT_MESSAGE_DELETED'];
      if (!relevantTypes.includes(eventType)) return;

      // Try to match by thread uid or transaction uid
      const threadMatch = raw.threadUid === thread.uid || raw.transactionUid === thread.transactionId || raw.transactionKey === thread.transactionId;
      if (!threadMatch) return;

      // Do not interrupt edit mode (inline editor)
      if (editingMessage) return;

      // Refresh messages for current thread
      (async () => {
        try {
          const resp = await getChatMessages(thread.uid, 1, 50);
          if (resp?.success) {
            const newMessages: ChatMessage[] = resp.data?.messages || resp.data || [];
            setMessages(newMessages.slice(0, MAX_MESSAGES_IN_MEMORY));
          }
        } catch (err) {
          console.error('Realtime refresh error:', err);
        }
      })();
    };

    const unsub = onChildAdded(notificationsRef, handler as any);

    return () => {
      // Only detach THIS listener. off(notificationsRef) with no callback removed
      // every listener registered on pending-communications/{uid} — including
      // other components' (e.g. usePendingCommunicationRealtime, another open
      // window) — silently killing their realtime updates.
      try { unsub && unsub(); } catch {}
    };
  }, [database, user?.uid, thread?.uid, thread?.transactionId, editingMessage, getChatMessages]);

  // Track previous thread uid to detect actual thread changes
  const prevThreadUidRef = useRef<string | null>(null);

  useEffect(() => {
    if (open && thread?.uid) {
      // Only reset state when thread actually changes (not on every render)
      if (prevThreadUidRef.current !== thread.uid) {
        setPage(1);
        setIsMinimized(false);
        setUnreadCount(0);
        loadMessages(1, false);
        prevThreadUidRef.current = thread.uid;
      }
    }
  }, [open, thread?.uid, loadMessages]);

  useEffect(() => {
    if (!loading && messages.length > 0 && !isMinimized) {
      scrollToBottom();
    }
  }, [messages, loading, isMinimized]);

  // Handle expanding from minimized state
  const handleExpand = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setIsMinimized(false);
    setUnreadCount(0);
    if (thread?.uid) {
      markChatMessagesAsRead(thread.uid, { markAll: true });
    }
    setTimeout(scrollToBottom, 100);
  };

  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsMinimized(true);
  };

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClose();
  };

  // File attachment handlers
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setPendingFiles(prev => [...prev, ...fileArray]);
    }
    // Reset after capturing — defer to avoid clearing FileList before state update
    setTimeout(() => { if (fileInputRef.current) fileInputRef.current.value = ''; }, 0);
  };

  const handleRemovePendingFile = (index: number) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragOver(false);
    if (e.dataTransfer.files) setPendingFiles(prev => [...prev, ...Array.from(e.dataTransfer.files)]);
  };

  const handleAttachmentView = async (attachment: ChatAttachment) => {
    try {
      const response = await getSignedDownloadUrl(attachment.filePath, attachment.fileName);
      if (response.success) { window.open(response.data?.url || response.data, '_blank'); return; }
    } catch { /* fallback */ }
    window.open(attachment.publicUrl, '_blank');
  };

  const handleAttachmentDownload = async (attachment: ChatAttachment) => {
    try {
      const response = await getSignedDownloadUrl(attachment.filePath, attachment.fileName);
      const url = response.success ? (response.data?.url || response.data) : attachment.publicUrl;
      const a = document.createElement('a'); a.href = url; a.download = attachment.fileName; a.target = '_blank';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch { window.open(attachment.publicUrl, '_blank'); }
  };

  const isImageFile = (mimeType: string) => mimeType?.startsWith('image/');
  const isPdfFile = (mimeType: string) => mimeType === 'application/pdf';
  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  const getFileIcon = (mimeType: string) => {
    if (isImageFile(mimeType)) return <ImageIcon sx={{ fontSize: 18 }} />;
    if (isPdfFile(mimeType)) return <PictureAsPdfIcon sx={{ fontSize: 18 }} />;
    return <InsertDriveFileIcon sx={{ fontSize: 18 }} />;
  };

  // Editor content change handler
  const handleEditorContentChange = (text: string, html: string) => {
    setNewMessage(text);
    setEditorHtml(html);
  };

  const handleEditorMentionQuery = (query: string | null) => {
    if (query !== null) {
      setMentionQuery(query);
      setShowMentionDropdown(true);
    } else {
      setShowMentionDropdown(false);
      setMentionQuery('');
    }
  };

  const handleSelectMention = (participant: ChatParticipant) => {
    const name = `${participant.firstName} ${participant.lastName}`.trim();
    const editorRoot = document.querySelector('.chat-editor-root') as any;
    if (editorRoot?.__insertMention) editorRoot.__insertMention(name);
    if (!mentions.find(m => m.uid === participant.uid)) {
      setMentions(prev => [...prev, { uid: participant.uid, name }]);
    }
    setShowMentionDropdown(false);
    setMentionQuery('');
  };

  const filteredMentionParticipants = currentParticipants.filter(p => {
    if (p.uid === user?.uid) return false;
    if (!mentionQuery) return true;
    return `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase().includes(mentionQuery);
  });
  
  const TranslatedMarkdownMessage = ({ message }: { message: ChatMessage }) => {
    const [translatedContent, setTranslatedContent] = useState(message.message || '');
  
    useEffect(() => {
      let cancelled = false;
      smartTranslate(message.message || '', language).then((result) => {
        if (!cancelled) setTranslatedContent(result);
      });
      return () => { cancelled = true; };
    }, [message.message, language]);
  
    return (
      <MarkdownMessage
        content={translatedContent}
        mentions={message.mentions}
        isOwn={isOwnMessage(message)}
        fontSize={13}
      />
    );
  };
  
  const renderMessageWithMentions = (message: ChatMessage) => {
    return <TranslatedMarkdownMessage message={message} />;
  };

  const handleSend = async () => {
    if ((!newMessage.trim() && pendingFiles.length === 0) || !thread?.uid) return;

    if (!currentUserUid || (!isCurrentUserParticipant && !isCurrentUserCreator)) {
      showToast('You no longer have access to this chat', 'error');
      return;
    }

    const markdown = htmlToMarkdown(editorHtml);
    const messageText = markdown || newMessage.trim() || (pendingFiles.length > 0 ? `Sent ${pendingFiles.length} file${pendingFiles.length > 1 ? 's' : ''}` : '');
    const filesToUpload = [...pendingFiles];
    console.log('pending files to upload:', filesToUpload);
    const messageMentions = [...mentions];
    setNewMessage('');
    setEditorHtml('');
    setClearTrigger(prev => prev + 1);
    setPendingFiles([]);
    setMentions([]);
    setShowMentionDropdown(false);
    setSending(true);

    // Check if current user is a participant, if not, add them first
    const isParticipant = currentParticipants.some(p => p.uid === user?.uid);
    if (!isParticipant && user?.uid) {
      try {
        const addResponse = await addChatParticipants(thread.uid, [user.uid]);
        if (addResponse.success) {
          const newParticipant: ChatParticipant = {
            uid: user.uid, firstName: user.firstName || '', lastName: user.lastName || '',
            email: user.email || '', resourceType: user.resourceType || ''
          };
          setCurrentParticipants(prev => [...prev, newParticipant]);
        }
      } catch (error) { console.error('Error auto-adding user as participant:', error); }
    }

    // Upload files if any
    let attachments: ChatAttachment[] = [];
    if (filesToUpload.length > 0) {
      setUploading(true);
      try {
        console.log('Uploading files:', filesToUpload);
        const uploadResponse = await uploadChatFiles(filesToUpload);
        if (uploadResponse.success) { attachments = uploadResponse.data; }
        else { showToast('Failed to upload files', 'error'); setSending(false); setUploading(false); return; }
      } catch (error) {
        console.error('Error uploading files:', error);
        showToast('Failed to upload files', 'error'); setSending(false); setUploading(false); return;
      }
      setUploading(false);
    }

    const messageType = attachments.length > 0
      ? (attachments.every(a => isImageFile(a.mimeType)) ? 'IMAGE' : 'FILE') : 'TEXT';

    // Optimistic update
    const tempMessage: ChatMessage = {
      uid: `temp-${Date.now()}`, threadId: thread.uid,
      senderId: user?.uid || '', senderName: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
      message: messageText, messageType, messageStatus: 'SENT',
      readBy: [], deliveredTo: [], attachments, mentions: messageMentions,
      isDeleted: false, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString()
    };
    setMessages(prev => {
      const updated = [...prev, tempMessage];
      return updated.length > MAX_MESSAGES_IN_MEMORY ? updated.slice(-MAX_MESSAGES_IN_MEMORY) : updated;
    });
    scrollToBottom();

    try {
      const response = await sendChatMessage(thread.uid, {
        message: messageText, messageType,
        attachments: attachments.length > 0 ? attachments : undefined,
        mentions: messageMentions.length > 0 ? messageMentions : undefined
      });
      if (response.success) {
        setMessages(prev => prev.map(m => (m.uid === tempMessage.uid ? response.data : m)));
      } else {
        setMessages(prev => prev.filter(m => m.uid !== tempMessage.uid));
        showToast('Failed to send message', 'error');
      }
    } catch (error) {
      if (isAccessError(error)) {
        showToast('You no longer have access to this chat', 'error');
        return;
      }
      console.error('Error sending message:', error);
      setMessages(prev => prev.filter(m => m.uid !== tempMessage.uid));
      showToast('Failed to send message', 'error');
    } finally { setSending(false); }
  };


  // Participants panel functions
  const loadAvailableParticipants = useCallback(async (participantsOverride?: ChatParticipant[]) => {
    if (!thread?.transactionId) return;

    setLoadingParticipants(true);
    try {
      const response = await getChatParticipants(thread.transactionId);
      if (response.success) {
        // Filter out participants that are already in the chat
        const existingUids = (participantsOverride || currentParticipants).map(p => p.uid);
        const available = (response.data || []).filter(
          (p: ChatParticipant) => !existingUids.includes(p.uid)
        );
        setAvailableParticipants(available);
      }
    } catch (error) {
      console.error('Error loading available participants:', error);
      showToast('Failed to load participants', 'error');
    } finally {
      setLoadingParticipants(false);
    }
  }, [thread?.transactionId, getChatParticipants, currentParticipants, showToast]);

  const loadChatGroups = useCallback(async () => {
    if (!thread?.transactionId) return;
    try {
      const response = await getChatGroups(thread.transactionId);
      if (response.success) setChatGroups(response.data || []);
    } catch { /* silent */ }
  }, [thread?.transactionId, getChatGroups]);

  const handleSelectGroupForAdd = (members: any[]) => {
    const memberUids = members.map((m: any) => m.uid).filter(Boolean);
    const existingUids = currentParticipants.map(p => p.uid);
    const newUids = memberUids.filter((uid: string) => !existingUids.includes(uid));
    setSelectedNewParticipants(prev => {
      const combined = new Set([...prev, ...newUids]);
      return Array.from(combined);
    });
    showToast(`Selected ${newUids.length} group members`, 'success');
  };

  const handleOpenParticipantsPanel = () => {
    setShowParticipantsPanel(true);
    setSelectedNewParticipants([]);
    loadAvailableParticipants();
    loadChatGroups();
  };

  const handleCloseParticipantsPanel = () => {
    setShowParticipantsPanel(false);
    setSelectedNewParticipants([]);
    setParticipantSearchQuery('');
    setRemovalDialogOpen(false);
    setRemovalTargetUids([]);
    setRemovalMode('remove');
  };

  // Filter available participants based on search
  const filteredAvailableParticipants = availableParticipants.filter(p => {
    if (!participantSearchQuery) return true;
    const query = participantSearchQuery.toLowerCase();
    const fullName = `${p.firstName || ''} ${p.lastName || ''}`.toLowerCase();
    return (
      fullName.includes(query) ||
      (p.email && p.email.toLowerCase().includes(query)) ||
      (p.resourceType && p.resourceType.toLowerCase().includes(query))
    );
  });

  const handleToggleNewParticipant = (uid: string) => {
    setSelectedNewParticipants(prev =>
      prev.includes(uid)
        ? prev.filter(p => p !== uid)
        : [...prev, uid]
    );
  };

  const handleAddParticipants = async () => {
    if (selectedNewParticipants.length === 0 || !thread?.uid) return;

    setAddingParticipants(true);
    try {
      const response = await addChatParticipants(thread.uid, selectedNewParticipants);
      if (response.success) {
        // Update current participants list
        const addedParticipants = availableParticipants.filter(
          p => selectedNewParticipants.includes(p.uid)
        );
        setCurrentParticipants(prev => [...prev, ...addedParticipants]);
        setAvailableParticipants(prev => 
          prev.filter(p => !selectedNewParticipants.includes(p.uid))
        );
        setSelectedNewParticipants([]);
        showToast('Participants added successfully', 'success');
      } else {
        showToast('Failed to add participants', 'error');
      }
    } catch (error) {
      console.error('Error adding participants:', error);
      showToast('Failed to add participants', 'error');
    } finally {
      setAddingParticipants(false);
    }
  };

  const handleConfirmRemoval = async () => {
    if (!thread?.uid || removalTargetUids.length === 0 || removalInProgress) return;

    setRemovalInProgress(true);
    try {
      const response = await removeChatParticipants(thread.uid, removalTargetUids);
      if (response?.success === false) {
        throw new Error(response?.message || 'Failed to remove participant(s)');
      }

      const nextParticipants = response?.thread?.participants
        || currentParticipants.filter(participant => !removalTargetUids.includes(participant.uid));

      setCurrentParticipants(nextParticipants);
      setSelectedNewParticipants(prev => prev.filter(uid => !removalTargetUids.includes(uid)));
      await loadAvailableParticipants(nextParticipants);

      const removedNames = currentParticipants
        .filter(participant => removalTargetUids.includes(participant.uid))
        .map(getParticipantName);
      const isLeaving = removalMode === 'leave' || removalTargetUids.includes(currentUserUid || '');

      if (isLeaving) {
        showToast('You left the chat', 'success');
        handleCloseParticipantsPanel();
        closeChatSurface();
        return;
      }

      showToast(`Removed ${removedNames.join(', ')} from the chat`, 'success');
      await loadMessages(1, false);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 403) {
        showToast('Not authorized to remove this user', 'error');
      } else if (status === 404) {
        showToast('Thread not found', 'error');
      } else {
        showToast('Failed to remove participant(s)', 'error');
      }
    } finally {
      setRemovalInProgress(false);
      setRemovalDialogOpen(false);
      setRemovalTargetUids([]);
      setRemovalMode('remove');
    }
  };

  // Update current participants when thread changes
  useEffect(() => {
    if (thread?.participants) {
      setCurrentParticipants(thread.participants);
    }
  }, [thread?.participants]);

  // Edit message handlers
  const handleEditMessage = async () => {
    const currentEditingMessage = editingMessage;
    const messageUid = currentEditingMessage ? getMessageUid(currentEditingMessage) : '';
    if (!messageUid || !editText.trim() || editInProgress || !currentEditingMessage) return;

    const prevText = currentEditingMessage.message || '';

    // Optimistic update
    setMessages(prev => prev.map(m => getMessageUid(m) === messageUid ? { ...m, message: editText, updatedAt: new Date().toISOString() } : m));
    setEditInProgress(true);
    try {
      const response = await editChatMessage(messageUid, { message: editText });
      if (response?.success) {
        const updated = response.data as ChatMessage;
        // Replace with server response if available
        setMessages(prev => prev.map(m => getMessageUid(m) === messageUid ? { ...m, ...updated, uid: getMessageUid(updated) || messageUid } : m));
        setEditingMessage(null);
        setEditText('');
        showToast('Message edited', 'success');
      } else {
        throw new Error(response?.message || 'Failed to edit message');
      }
    } catch (error: any) {
      console.error('Edit (optimistic) failed (inline):', error);
      // rollback
      setMessages(prev => prev.map(m => getMessageUid(m) === messageUid ? { ...m, message: prevText } : m));
      const apiMsg = error?.response?.data?.message || error?.message;
      showToast(apiMsg || 'Failed to edit message', 'error');
    } finally {
      setEditInProgress(false);
    }
  };

  const handleDeleteWithConfirm = async (message: ChatMessage) => {
    if (!message?.uid) return;
    const ok = window.confirm('Delete this message?');
    if (!ok) return;

    const msgUid = message.uid;
    const prevMessageText = message.message;

    setDeletingMessageUid(msgUid);

    // Optimistic update
    setMessages(prev => prev.map(m => m.uid === msgUid ? { ...m, isDeleted: true, message: '(this message was deleted)' } : m));

    try {
      const response = await deleteChatMessage(msgUid);
      if (response?.success) {
        showToast('Message deleted', 'success');
      } else {
        throw new Error(response?.message || 'Failed to delete message');
      }
    } catch (error: any) {
      console.error('Delete (optimistic) failed (inline):', error);
      // rollback
      setMessages(prev => prev.map(m => m.uid === msgUid ? { ...m, isDeleted: false, message: prevMessageText } : m));
      const apiMsg = error?.response?.data?.message || error?.message;
      showToast(apiMsg || 'Failed to delete message', 'error');
    }
    finally {
      setDeletingMessageUid(null);
    }
  };

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      const nextPage = page + 1;
      setPage(nextPage);
      loadMessages(nextPage, true);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
    
    // If today, show only time
    if (date.toDateString() === today.toDateString()) {
      return timeStr;
    }
    // If yesterday, show "Yesterday, time"
    else if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${timeStr}`;
    }
    // Otherwise show "date, time"
    else {
      const dateStr = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
      return `${dateStr}, ${timeStr}`;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'short',
        day: 'numeric'
      });
    }
  };

  const shouldShowDateSeparator = (currentMsg: ChatMessage, prevMsg?: ChatMessage) => {
    if (!prevMsg) return true;
    const currentDate = new Date(currentMsg.createdAt).toDateString();
    const prevDate = new Date(prevMsg.createdAt).toDateString();
    return currentDate !== prevDate;
  };

  const isOwnMessage = (message: ChatMessage) => {
    const senderId = typeof message.senderId === 'string' 
      ? message.senderId 
      : (message.senderId as ChatParticipant)?.uid;
    return senderId === user?.uid || message.senderName === `${user?.firstName} ${user?.lastName}`;
  };

  const getMessageUid = (message: ChatMessage) => message.uid || message.id || '';

  

  const getInitials = (name: string) => {
    if (!name) return 'U';
    const words = name.split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getParticipantCount = () => {
    return thread?.participants?.length || 0;
  };

  if (!open) return null;

  // Calculate position based on window index (each window is 360px wide + 10px gap)
  const rightPosition = 420 + (windowIndex * 370);

  return (
    <Paper
      elevation={embedded ? 0 : 8}
      sx={{
        position: embedded ? 'relative' : 'fixed',
        bottom: embedded ? 'auto' : 0,
        right: embedded ? 'auto' : rightPosition,
        width: embedded ? '100%' : 360,
        height: embedded ? '100%' : 'auto',
        zIndex: embedded ? 'auto' : 1400 + (10 - windowIndex),
        borderRadius: embedded ? 0 : '12px 12px 0 0',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        boxShadow: embedded ? 'none' : '0 -4px 20px rgba(0,0,0,0.15)'
      }}
    >
      {/* Hidden file input — mounted at root so it persists across Collapse re-renders */}
      <input type="file" ref={fileInputRef} onChange={handleFileSelect} multiple style={{ display: 'none' }} />

      {/* Header - Always visible */}
      <Box
        onClick={isMinimized ? () => handleExpand() : undefined}
        sx={{
          p: 1,
          bgcolor: 'primary.main',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: isMinimized ? 'pointer' : 'default',
          '&:hover': isMinimized ? { bgcolor: 'primary.dark' } : {}
        }}
      >
        {/* Back arrow — only in embedded mode */}
        {embedded && !isMinimized && (
          <IconButton sx={{ color: 'white', p: 0.5 }} size="small" onClick={(e) => { e.stopPropagation(); onClose(); }}>
            <ArrowBackIcon sx={{ fontSize: 20 }} />
          </IconButton>
        )}

        <Badge badgeContent={unreadCount} color="error" invisible={!isMinimized || unreadCount === 0}>
          <Avatar
            sx={{
              width: 32,
              height: 32,
              bgcolor: 'primary.light',
              color: 'primary.main',
              fontSize: 12
            }}
          >
            {getParticipantCount() > 2 ? <GroupIcon sx={{ fontSize: 18 }} /> : getInitials(thread?.subject || 'CH')}
          </Avatar>
        </Badge>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>
            {thread?.subject || 'Chat'}
          </Typography>
          {!isMinimized && (
            <Tooltip title="View & Add Participants">
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenParticipantsPanel();
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  opacity: 0.85,
                  fontSize: 10,
                  cursor: 'pointer',
                  px: 0.75,
                  py: 0.25,
                  my: 0.5,
                  borderRadius: 1,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    opacity: 1,
                    bgcolor: 'rgba(255,255,255,0.25)',
                  }
                }}
              >
                <GroupIcon sx={{ fontSize: 12 }} />
                <Typography variant="caption" sx={{ fontSize: 10, lineHeight: 1 }}>
                  {currentParticipants.length}
                </Typography>
                <PersonAddIcon sx={{ fontSize: 11, ml: 0.25 }} />
              </Box>
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {!isMinimized && thread?.transactionId && (
            <Tooltip title="Call user">
              <span>
                <IconButton
                  sx={{ color: 'white', p: 0.5 }}
                  size="small"
                  onClick={(e) => { e.stopPropagation(); openCallDialog(thread.transactionId); }}
                >
                  <CallIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          )}
          {isMinimized ? (
            <Tooltip title="Expand">
              <IconButton sx={{ color: 'white', p: 0.5 }} size="small" onClick={(e) => handleExpand(e)}>
                <OpenInFullIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ) : embedded ? (
            /* Embedded: X closes the entire panel */
            <Tooltip title="Close">
              <IconButton sx={{ color: 'white', p: 0.5 }} size="small"
                onClick={(e) => { e.stopPropagation(); onClosePanel ? onClosePanel() : onClose(); }}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ) : (
            /* Floating window: minimize + close */
            <>
              <Tooltip title="Minimize">
                <IconButton sx={{ color: 'white', p: 0.5 }} size="small" onClick={handleMinimize}>
                  <RemoveIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Close">
                <IconButton sx={{ color: 'white', p: 0.5 }} size="small" onClick={handleClose}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
      </Box>

      {/* Collapsible Content */}
      <Collapse in={!isMinimized} sx={embedded
        ? { flex: 1, display: !isMinimized ? 'flex' : 'none', flexDirection: 'column', minHeight: 0, overflow: 'hidden',
            '& .MuiCollapse-wrapper': { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 },
            '& .MuiCollapse-wrapperInner': { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 },
            '& .MuiCollapse-entered': { height: '100% !important' },
          }
        : {}}>

        {/* Participants Panel - Slides in when open */}
        {showParticipantsPanel ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', height: embedded ? '100%' : 450, flex: embedded ? 1 : 'none' }}>
            {/* Participants Panel Header */}
            <Box
              sx={{
                p: 1.5,
                bgcolor: 'grey.100',
                borderBottom: 1,
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <IconButton size="small" onClick={handleCloseParticipantsPanel}>
                <ArrowBackIcon sx={{ fontSize: 20 }} />
              </IconButton>
              <Typography variant="subtitle2" fontWeight={600}>
                Participants ({currentParticipants.length})
              </Typography>
            </Box>

            {/* Current Participants List */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <Typography variant="caption" sx={{ px: 1.5, pt: 1.5, display: 'block', color: 'text.secondary', fontWeight: 600 }}>
                Current Members
              </Typography>
              <List dense disablePadding>
                {currentParticipants.map((participant) => (
                  <ListItem
                    key={participant.uid}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      '&:hover .participant-action': {
                        opacity: 1,
                      },
                    }}
                  >
                    <ListItemAvatar sx={{ minWidth: 36 }}>
                      <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: 'primary.light', color: 'primary.main' }}>
                        {getInitials(`${participant.firstName} ${participant.lastName}`)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${participant.firstName} ${participant.lastName}`}
                      secondary={participant.resourceType || participant.email}
                      primaryTypographyProps={{ variant: 'body2', fontSize: 13 }}
                      secondaryTypographyProps={{ variant: 'caption', fontSize: 10 }}
                    />
                    {participant.uid === currentUserUid && (
                      <Chip label="You" size="small" sx={{ height: 18, fontSize: 9 }} />
                    )}
                    {participant.uid === currentUserUid ? (
                      <Button
                        className="participant-action"
                        size="small"
                        color="error"
                        onClick={() => queueParticipantRemoval(participant.uid, 'leave')}
                        disabled={removalInProgress}
                        sx={{ ml: 1, opacity: 0, textTransform: 'none', fontSize: 10 }}
                      >
                        Leave Chat
                      </Button>
                    ) : canManageParticipants && !isThreadCreator(participant.uid) ? (
                      <Button
                        className="participant-action"
                        size="small"
                        color="error"
                        onClick={() => queueParticipantRemoval(participant.uid, 'remove')}
                        disabled={removalInProgress}
                        sx={{ ml: 1, opacity: removalTargetUids.includes(participant.uid) ? 1 : 0, textTransform: 'none', fontSize: 10 }}
                      >
                        {removalTargetUids.includes(participant.uid) ? 'Selected' : 'Remove'}
                      </Button>
                    ) : null}
                  </ListItem>
                ))}
              </List>

              {/* Divider */}
              <Divider sx={{ my: 1 }} />

              {/* Add Participants Section */}
              <Typography variant="caption" sx={{ px: 1.5, display: 'block', color: 'text.secondary', fontWeight: 600 }}>
                Add Participants
              </Typography>
              
              {/* Search Input */}
              <Box sx={{ px: 1.5, py: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search participants..."
                  value={participantSearchQuery}
                  onChange={(e) => setParticipantSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" sx={{ fontSize: 16 }} />
                      </InputAdornment>
                    )
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'grey.100',
                      '& input': { py: 0.5, fontSize: 12 }
                    }
                  }}
                />
                {/* Select All Option */}
                {filteredAvailableParticipants.length > 0 && (
                  <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      onClick={() => {
                        const filteredUids = filteredAvailableParticipants.map(p => p.uid);
                        const allSelected = filteredUids.every(uid => selectedNewParticipants.includes(uid));
                        if (allSelected) {
                          setSelectedNewParticipants(prev => prev.filter(uid => !filteredUids.includes(uid)));
                        } else {
                          setSelectedNewParticipants(prev => {
                            const newSelection = [...prev];
                            filteredUids.forEach(uid => {
                              if (!newSelection.includes(uid)) {
                                newSelection.push(uid);
                              }
                            });
                            return newSelection;
                          });
                        }
                      }}
                      sx={{ textTransform: 'none', fontSize: 10, py: 0 }}
                    >
                      {filteredAvailableParticipants.every(p => selectedNewParticipants.includes(p.uid)) 
                        ? 'Deselect All' 
                        : `Select All (${filteredAvailableParticipants.length})`}
                    </Button>
                  </Box>
                )}
              </Box>
              
              {/* Quick Add from Groups */}
              {chatGroups.length > 0 && (
                <Box sx={{ px: 1.5, pb: 1 }}>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', fontWeight: 600, fontSize: 10, mb: 0.5 }}>
                    Add from Group
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {chatGroups.map((group: any) => (
                      <Chip key={group.uid} label={`${group.name} (${group.members?.length || 0})`}
                        size="small" variant="outlined" color="primary" clickable
                        icon={<GroupIcon sx={{ fontSize: 14 }} />}
                        onClick={() => handleSelectGroupForAdd(group.members || [])}
                        sx={{ height: 24, fontSize: 10, '& .MuiChip-label': { px: 0.5 } }}
                      />
                    ))}
                  </Box>
                  <Divider sx={{ mt: 1 }} />
                </Box>
              )}

              {loadingParticipants ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                  <LinearProgress sx={{ width: '80%' }} />
                </Box>
              ) : filteredAvailableParticipants.length === 0 ? (
                <Box sx={{ px: 1.5, py: 2, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary" fontSize={12}>
                    {participantSearchQuery ? 'No participants found' : 'No more participants available to add'}
                  </Typography>
                </Box>
              ) : (
                <List dense disablePadding>
                  {filteredAvailableParticipants.map((participant) => (
                    <ListItemButton
                      key={participant.uid}
                      onClick={() => handleToggleNewParticipant(participant.uid)}
                      sx={{ px: 1.5, py: 0.5 }}
                    >
                      <Checkbox
                        checked={selectedNewParticipants.includes(participant.uid)}
                        size="small"
                        sx={{ p: 0.5, mr: 1 }}
                      />
                      <ListItemAvatar sx={{ minWidth: 36 }}>
                        <Avatar sx={{ width: 28, height: 28, fontSize: 11, bgcolor: 'grey.300' }}>
                          {getInitials(`${participant.firstName} ${participant.lastName}`)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={`${participant.firstName} ${participant.lastName}`}
                        secondary={participant.resourceType || participant.email}
                        primaryTypographyProps={{ variant: 'body2', fontSize: 13 }}
                        secondaryTypographyProps={{ variant: 'caption', fontSize: 10 }}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>

            {/* Footer actions - Always visible */}
            <Box sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', bgcolor: 'white', display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<LogoutIcon />}
                onClick={() => queueParticipantRemoval(currentUserUid || '', 'leave')}
                disabled={removalInProgress || !currentUserUid}
                sx={{ flex: 1, py: 0.5, borderRadius: 2, textTransform: 'none', fontWeight: 500, fontSize: 12 }}
              >
                Leave Chat
              </Button>
              <Button
                variant="contained"
                fullWidth
                startIcon={<PersonAddIcon />}
                onClick={handleAddParticipants}
                disabled={addingParticipants || selectedNewParticipants.length === 0 || removalInProgress}
                sx={{ flex: 1, py: 0.5, borderRadius: 2, textTransform: 'none', fontWeight: 500, fontSize: 12 }}
              >
                {addingParticipants 
                  ? 'Adding...' 
                  : selectedNewParticipants.length > 0 
                    ? `Add ${selectedNewParticipants.length} Participant${selectedNewParticipants.length > 1 ? 's' : ''}`
                    : 'Add Participants'
                }
              </Button>
            </Box>

            <Dialog open={removalDialogOpen} onClose={cancelRemoval} maxWidth="xs" fullWidth sx={{ zIndex: 9999 }}>
              <DialogTitle>
                {removalMode === 'leave' ? 'Leave chat?' : 'Remove participant(s)?'}
              </DialogTitle>
              <DialogContent>
                <Typography variant="body2" color="text.secondary">
                  {removalMode === 'leave'
                    ? 'You will be removed from this chat and will stop receiving new messages.'
                    : `This will remove ${removalTargetUids.length} participant${removalTargetUids.length > 1 ? 's' : ''} from the chat.`}
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={cancelRemoval} disabled={removalInProgress}>
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirmRemoval}
                  color="error"
                  variant="contained"
                  disabled={removalInProgress || removalTargetUids.length === 0}
                >
                  {removalInProgress
                    ? 'Processing...'
                    : removalMode === 'leave'
                      ? 'Leave Chat'
                      : `Remove ${removalTargetUids.length > 1 ? 'Participants' : 'Participant'}`}
                </Button>
              </DialogActions>
            </Dialog>



            {/* Delete is handled via window.confirm and optimistic update */}
          </Box>
        ) : (
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={{ display: 'flex', flexDirection: 'column', flex: embedded ? 1 : 'none', minHeight: 0, position: 'relative' }}>
            {/* Drag overlay — covers full chat area */}
            {isDragOver && (
              <Box sx={{
                position: 'absolute', inset: 0, zIndex: 10, bgcolor: 'rgba(25, 118, 210, 0.1)',
                border: '2px dashed', borderColor: 'primary.main', borderRadius: 1,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <Typography color="primary" fontWeight={600}>Drop files here</Typography>
              </Box>
            )}
            {/* Slim nav loader for refreshing */}
            <Box sx={{ height: 2 }}>
              {(loading || isRefreshing) && (
                <LinearProgress
                  sx={{
                    height: 1,
                    '& .MuiLinearProgress-bar': {
                      transition: 'none'
                    }
                  }}
                />
              )}
            </Box>

            {/* Transaction Info */}
            {transactionKey && (
              <Box sx={{ px: 1.5, py: 0.75, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="caption" component="span"
                  onClick={() => {
                    const teamUid = (thread as any).authkey?.teamUid || (thread as any).teamUid;
                    const txnUid = (thread as any).authkey?.uid || thread.transactionId;
                    if (teamUid) {
                      navigate(`/transactions/details/${teamUid}/${txnUid || ''}/${transactionKey}`);
                    } else {
                      console.warn('Missing teamUid for navigation. Thread data:', { authkey: (thread as any).authkey, transactionId: thread.transactionId });
                    }
                  }}
                  sx={{
                    fontSize: 11, fontWeight: 700, color: 'primary.main',
                    cursor: 'pointer', textDecoration: 'underline',
                    textDecorationStyle: 'dotted', textUnderlineOffset: 2,
                    '&:hover': { color: 'primary.dark', textDecorationStyle: 'solid' },
                  }}>
                  Transaction: {transactionKey}
                </Typography>
              </Box>
            )}

            {/* Messages */}
            <Box
              ref={messagesContainerRef}
              sx={{
                ...(embedded ? { flex: 1, minHeight: 0 } : { height: 350 }),
                overflow: 'auto',
                p: 1.5,
                bgcolor: 'grey.50',
                display: 'flex',
                flexDirection: 'column',
              }}
        >
          {/* Load More Button */}
          {hasMore && (
            <Box sx={{ textAlign: 'center', mb: 1.5 }}>
              <Chip
                label={loading ? 'Loading...' : 'Load earlier messages'}
                onClick={handleLoadMore}
                disabled={loading}
                variant="outlined"
                size="small"
                sx={{ cursor: 'pointer', fontSize: 10, height: 24 }}
              />
            </Box>
          )}

          {loading && messages.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
              <Typography variant="body2" color="text.secondary">Loading messages...</Typography>
            </Box>
          ) : messages.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No messages yet. Start the conversation!
              </Typography>
            </Box>
          ) : (
            messages.map((message, index) => {
              const prevMessage = index > 0 ? messages[index - 1] : undefined;
              const showDate = shouldShowDateSeparator(message, prevMessage);
              const isMine = isOwnMessage(message);
              const isSystemMessage = message.messageType === 'SYSTEM';
              const isEdited = Boolean(message.updatedAt && message.createdAt && message.updatedAt !== message.createdAt);

              if (isSystemMessage) {
                return (
                  <Box key={message.uid}>
                    {showDate && (
                      <Box sx={{ textAlign: 'center', my: 1.5 }}>
                        <Chip
                          label={formatDate(message.createdAt)}
                          size="small"
                          sx={{ bgcolor: 'grey.200', fontSize: 10, height: 20 }}
                        />
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                      <Paper
                        elevation={0}
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          borderRadius: 999,
                          bgcolor: 'grey.100',
                          border: 1,
                          borderColor: 'divider',
                          maxWidth: '92%',
                          textAlign: 'center'
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 10 }}>
                          {message.message || 'System message'}
                        </Typography>
                      </Paper>
                    </Box>
                  </Box>
                );
              }

              return (
                <Box key={getMessageUid(message) || `${message.createdAt}-${index}`}>
                  {/* Date Separator */}
                  {showDate && (
                    <Box sx={{ textAlign: 'center', my: 1.5 }}>
                      <Chip
                        label={formatDate(message.createdAt)}
                        size="small"
                        sx={{ bgcolor: 'grey.200', fontSize: 10, height: 20 }}
                      />
                    </Box>
                  )}

                  {/* Message Bubble */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: isMine ? 'row-reverse' : 'row',
                      alignItems: 'flex-end',
                      mb: 0.75,
                      gap: 0.75
                    }}
                  >
                    {!isMine && (
                      <Avatar
                        sx={{
                          width: 24,
                          height: 24,
                          fontSize: 10,
                          bgcolor: 'primary.light',
                          color: 'primary.main'
                        }}
                      >
                        {getInitials(message.senderName || 'U')}
                      </Avatar>
                    )}

                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, '&:hover .copy-btn': { opacity: 1 }, '&:hover .edit-btn': { opacity: 1 }, '&:hover .delete-btn': { opacity: 1 }, minWidth: 0, maxWidth: '90%' }} data-msg-row>
                    {isMine && (
                      <>
                        {!message.isDeleted && (
                          <Tooltip title="Edit">
                            <IconButton className="edit-btn" size="small"
                              onClick={() => {
                                setEditingMessage(message);
                                setEditText(message.message || '');
                              }}
                              disabled={editInProgress}
                              sx={{ opacity: 0, transition: 'opacity 0.2s', mt: 0.25, p: 0.3 }}
                            >
                              <EditIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {!message.isDeleted && (
                          <Tooltip title="Delete">
                            <IconButton className="delete-btn" size="small"
                              onClick={() => handleDeleteWithConfirm(message)}
                              disabled={deleteInProgress}
                              sx={{ opacity: 0, transition: 'opacity 0.2s', mt: 0.25, p: 0.3 }}
                            >
                              <DeleteIcon sx={{ fontSize: 12, color: 'error.main' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </>
                    )}
                    {isMine && !message.isDeleted && (
                      <Tooltip title="Copy">
                        <IconButton className="copy-btn" size="small"
                          onClick={(e) => {
                            const row = (e.currentTarget as HTMLElement).closest('[data-msg-row]') as HTMLElement;
                            const contentEl = row?.querySelector('.msg-content') as HTMLElement;
                            if (contentEl) {
                              copyRichText(contentEl, message.message || '').then(() => showToast('Copied', 'success'));
                            } else {
                              navigator.clipboard.writeText(message.message || '');
                              showToast('Copied', 'success');
                            }
                          }}
                          sx={{ opacity: 0, transition: 'opacity 0.2s', mt: 0.25, p: 0.3 }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1,
                        borderRadius: 2,
                        bgcolor: isMine ? 'primary.main' : 'white',
                        color: isMine ? 'white' : 'text.primary',
                        borderBottomRightRadius: isMine ? 4 : 12,
                        borderBottomLeftRadius: isMine ? 12 : 4,
                        overflow: 'hidden',
                        minWidth: 0,
                      }}
                    >
                      {!isMine && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 600,
                            color: 'primary.main',
                            display: 'block',
                            mb: 0.25,
                            fontSize: 10
                          }}
                        >
                          {message.senderName}
                        </Typography>
                      )}
                      {getMessageUid(message) === (editingMessage ? getMessageUid(editingMessage) : '') ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        <TextField
                          fullWidth
                          multiline
                          size="small"
                          value={editText}
                          onChange={(e) => setEditText(e.target.value)}
                          disabled={editInProgress}
                          autoFocus
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: isMine ? 'rgba(255,255,255,0.1)' : 'grey.100',
                              color: isMine ? 'white' : 'text.primary',
                              '& fieldset': {
                                borderColor: isMine ? 'rgba(255,255,255,0.3)' : 'action.disabled'
                              },
                              '&:hover fieldset': {
                                borderColor: isMine ? 'rgba(255,255,255,0.5)' : 'action.active'
                              }
                            },
                            '& .MuiOutlinedInput-input': {
                              color: isMine ? 'white' : 'text.primary',
                              fontSize: 12,
                              padding: '6px 10px'
                            }
                          }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Button
                            size="small"
                            variant="text"
                            onClick={() => {
                              setEditingMessage(null);
                              setEditText('');
                            }}
                            disabled={editInProgress}
                            sx={{ color: isMine ? 'white' : 'text.primary', fontSize: 10, padding: '2px 6px' }}
                          >
                            Cancel
                          </Button>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={handleEditMessage}
                            disabled={editInProgress || !editText.trim() || editText === message.message}
                            sx={{ fontSize: 10, padding: '2px 8px',color:  'white' }}
                          >
                            {editInProgress ? 'Saving...' : 'Save'}
                          </Button>
                        </Box>
                      </Box>
                    ) : (
                      <Box className="msg-content">{renderMessageWithMentions(message)}</Box>
                    )}

                      {/* Attachments */}
                      {message.attachments && message.attachments.length > 0 && (
                        <Box sx={{ mt: 0.75, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {message.attachments.map((attachment, aIdx) => (
                            isImageFile(attachment.mimeType) ? (
                              <Box key={aIdx} sx={{ borderRadius: 1, overflow: 'hidden', cursor: 'pointer', position: 'relative', '&:hover .att-actions': { opacity: 1 } }}
                                onClick={() => handleAttachmentView(attachment)}>
                                <SignedImage filePath={attachment.filePath} fileName={attachment.fileName}
                                  fallbackUrl={attachment.publicUrl} getSignedUrl={getSignedImageUrl}
                                  style={{ maxWidth: '100%', maxHeight: 160, display: 'block', borderRadius: 4 }} />
                                <Box className="att-actions" sx={{
                                  position: 'absolute', top: 2, right: 2, display: 'flex', gap: 0.5, opacity: 0, transition: 'opacity 0.2s'
                                }}>
                                  <IconButton size="small" sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white', p: 0.25, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                                    onClick={(e) => { e.stopPropagation(); handleAttachmentView(attachment); }}>
                                    <OpenInNewIcon sx={{ fontSize: 12 }} />
                                  </IconButton>
                                  <IconButton size="small" sx={{ bgcolor: 'rgba(0,0,0,0.5)', color: 'white', p: 0.25, '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                                    onClick={(e) => { e.stopPropagation(); handleAttachmentDownload(attachment); }}>
                                    <DownloadIcon sx={{ fontSize: 12 }} />
                                  </IconButton>
                                </Box>
                              </Box>
                            ) : (
                              <Box key={aIdx} sx={{
                                display: 'flex', alignItems: 'center', gap: 0.75, p: 0.75,
                                borderRadius: 1, bgcolor: isMine ? 'rgba(255,255,255,0.15)' : 'grey.100',
                                cursor: 'pointer', '&:hover': { bgcolor: isMine ? 'rgba(255,255,255,0.25)' : 'grey.200' }
                              }} onClick={() => handleAttachmentView(attachment)}>
                                <Box sx={{ color: isMine ? 'white' : 'primary.main' }}>{getFileIcon(attachment.mimeType)}</Box>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                  <Typography variant="caption" noWrap sx={{ display: 'block', fontWeight: 600, fontSize: 10 }}>{attachment.fileName}</Typography>
                                  {attachment.fileSize && <Typography variant="caption" sx={{ opacity: 0.7, fontSize: 9 }}>{formatFileSize(attachment.fileSize)}</Typography>}
                                </Box>
                                <IconButton size="small" sx={{ color: isMine ? 'white' : 'text.secondary', p: 0.25 }}
                                  onClick={(e) => { e.stopPropagation(); handleAttachmentDownload(attachment); }}>
                                  <DownloadIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Box>
                            )
                          ))}
                        </Box>
                      )}

                      <Typography
                        variant="caption"
                        sx={{
                          display: 'block',
                          textAlign: 'right',
                          mt: 0.25,
                          opacity: 0.7,
                          fontSize: 9
                        }}
                      >
                        {formatTime(message.createdAt)}{isEdited ? ' • Edited' : ''}
                      </Typography>
                    </Paper>
                    {!isMine && !message.isDeleted && (
                      <Tooltip title="Copy">
                        <IconButton className="copy-btn" size="small"
                          onClick={(e) => {
                            const row = (e.currentTarget as HTMLElement).closest('[data-msg-row]') as HTMLElement;
                            const contentEl = row?.querySelector('.msg-content') as HTMLElement;
                            if (contentEl) {
                              copyRichText(contentEl, message.message || '').then(() => showToast('Copied', 'success'));
                            } else {
                              navigator.clipboard.writeText(message.message || '');
                              showToast('Copied', 'success');
                            }
                          }}
                          sx={{ opacity: 0, transition: 'opacity 0.2s', mt: 0.25, p: 0.3 }}
                        >
                          <ContentCopyIcon sx={{ fontSize: 12, color: 'text.secondary' }} />
                        </IconButton>
                      </Tooltip>
                    )}
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </Box>

        {/* Pending Attachments Preview */}
        {pendingFiles.length > 0 && (
          <Box sx={{ px: 1, pt: 0.75, borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50', display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {pendingFiles.map((file, idx) => (
              <Box key={idx} sx={{
                display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.25,
                bgcolor: 'white', borderRadius: 1, border: 1, borderColor: 'divider', maxWidth: 160
              }}>
                {file.type.startsWith('image/')
                  ? <Box component="img" src={URL.createObjectURL(file)} sx={{ width: 22, height: 22, borderRadius: 0.5, objectFit: 'cover' }} />
                  : <Box sx={{ color: 'primary.main' }}>{getFileIcon(file.type)}</Box>}
                <Typography variant="caption" noWrap sx={{ flex: 1, fontSize: 10 }}>{file.name}</Typography>
                <IconButton size="small" onClick={() => handleRemovePendingFile(idx)} sx={{ p: 0.15 }}>
                  <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {uploading && (
          <Box sx={{ px: 1, py: 0.25, display: 'flex', alignItems: 'center', gap: 0.5, bgcolor: 'grey.50' }}>
            <CircularProgress size={12} /><Typography variant="caption" color="text.secondary" fontSize={10}>Uploading...</Typography>
          </Box>
        )}

        {/* Message Input */}
        <Box
          sx={{
            p: 1,
            borderTop: pendingFiles.length === 0 ? 1 : 0,
            borderColor: 'divider',
            bgcolor: 'white',
            position: 'relative'
          }}
        >
          {/* Mention Dropdown */}
          {showMentionDropdown && filteredMentionParticipants.length > 0 && (
            <Paper elevation={4} sx={{
              position: 'absolute', bottom: '100%', left: 8, right: 8, mb: 0.5,
              maxHeight: 180, overflow: 'auto', zIndex: 10, borderRadius: 1.5
            }}>
              <List dense disablePadding>
                {filteredMentionParticipants.slice(0, 6).map((participant) => (
                  <ListItemButton key={participant.uid} onClick={() => handleSelectMention(participant)} sx={{ py: 0.5, px: 1 }}>
                    <ListItemAvatar sx={{ minWidth: 32 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: 10, bgcolor: 'primary.light', color: 'primary.main' }}>
                        {getInitials(`${participant.firstName} ${participant.lastName}`)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${participant.firstName} ${participant.lastName}`}
                      secondary={participant.resourceType || participant.email}
                      primaryTypographyProps={{ variant: 'body2', fontSize: 12 }}
                      secondaryTypographyProps={{ variant: 'caption', fontSize: 9 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Paper>
          )}

          <ChatEditor
            onSend={handleSend}
            onContentChange={handleEditorContentChange}
            onMentionQuery={handleEditorMentionQuery}
            onPasteFiles={(files) => setPendingFiles(prev => [...prev, ...files])}
            disabled={sending || uploading}
            maxRows={3}
            clearTrigger={clearTrigger}
            startAdornment={
              <IconButton size="small" onClick={() => fileInputRef.current?.click()} disabled={sending || uploading}>
                <AttachFileIcon sx={{ fontSize: 18, transform: 'rotate(45deg)' }} />
              </IconButton>
            }
            endAdornment={
              <IconButton
                onClick={handleSend}
                disabled={(!newMessage.trim() && pendingFiles.length === 0) || sending || uploading}
                color="primary"
                size="small"
              >
                <SendIcon sx={{ fontSize: 20, opacity: (sending || uploading) ? 0.5 : 1 }} />
              </IconButton>
            }
          />
        </Box>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

export default InlineChatWindow;
